/**
 * print-standings.ts - Standings Printing Functions
 *
 * Handles print functionality for standings and brackets,
 * generating print-optimized views with tournament headers.
 */

import { API_BASE_URL } from '../../utils/constants';
import { escapeHtml } from '../../utils/security';
import type { Tournament } from '../../../types/api';
import { t } from '../../utils/i18n-helper';
import {
  getPrintHeaderStyles,
  getPrintTableStyles,
  generateTournamentHeader,
  generatePrintFooter,
  showPrintOptionsDialog
} from '../print-utils';

/**
 * Print standings for a tournament
 * @param tournamentId - The tournament ID
 */
export async function printStandings(tournamentId: string): Promise<void> {
  const standingsDiv = document.getElementById(`standings-${tournamentId}`);
  if (!standingsDiv) return;

  // Show print options dialog
  const printOptions = await showPrintOptionsDialog();
  if (!printOptions) return; // User cancelled

  // Fetch tournament data to get all fields
  let tournament: Tournament;
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    tournament = await response.json();
  } catch (error) {
    console.error('Error fetching tournament:', error);
    const errorMsg = window.i18n ? window.i18n.t('standings.printDataLoadFailed') : 'Failed to load tournament data for printing';
    alert(errorMsg);
    return;
  }

  // Determine which round is being displayed
  let roundInfo = t('standings.title', 'Standings');
  const activeRoundBtn = standingsDiv.querySelector('.standings-round-selector .btn-primary');
  if (activeRoundBtn) {
    if (activeRoundBtn.textContent?.includes('Initial Ranking') || activeRoundBtn.textContent?.includes('Classement initial')) {
      roundInfo = t('standings.initialRanking', 'Initial Ranking');
    } else {
      // Try to extract round number from button
      const roundMatch = activeRoundBtn.textContent?.match(/(\d+)/);
      if (roundMatch && roundMatch[1]) {
        const roundNum = roundMatch[1];
        const afterRoundText = t('standings.afterRound', 'after Round');
        // Replace X placeholder with actual round number for languages that use it
        if (afterRoundText.includes('X')) {
          roundInfo = `${t('standings.title', 'Standings')} ${afterRoundText.replace('X', roundNum)}`;
        } else {
          roundInfo = `${t('standings.title', 'Standings')} ${afterRoundText} ${roundNum}`;
        }
      }
    }
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    const errorMsg = window.i18n ? window.i18n.t('standings.printWindowBlocked') : 'Failed to open print window. Please check your browser popup settings.';
    alert(errorMsg);
    return;
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>${tournament.name} - Standings</title>
        <style>
          ${getPrintHeaderStyles()}
          ${getPrintTableStyles()}

          /* Specific styles for standings */
          td {
            padding: 5px 10px;
            font-size: 13px;
            color: #1a1a1a;
            vertical-align: middle;
            line-height: 1.2;
          }

          /* Special formatting for standings */
          td small {
            display: inline !important;
            font-size: 0.85em;
            margin-left: 8px;
            color: #8a8a8a;
          }

          td br { display: none !important; }

          /* Category badges in print */
          .player-category-badges {
            display: inline-flex;
            gap: 2px;
            margin-left: 6px;
            vertical-align: middle;
          }

          .player-category-badge {
            display: inline-block;
            font-size: 7px;
            font-weight: 500;
            padding: 1px 3px;
            border-radius: 2px;
            background: #f0f0f0;
            color: #666;
            border: 1px solid #ddd;
          }

          /* Column widths for standings */
          th:nth-child(1), td:nth-child(1) {
            width: 40px;
            text-align: center;
          }

          td:nth-child(1) {
            font-weight: 700;
            font-size: 14px;
            color: #0a0a0a;
          }

          th:nth-child(2), td:nth-child(2) {
            width: auto;
          }

          td:nth-child(2) {
            font-weight: 500;
          }

          th:nth-child(3), td:nth-child(3) {
            width: 50px;
            text-align: center;
          }

          td:nth-child(3) {
            font-weight: 600;
            color: #0a0a0a;
          }

          th:nth-child(4), td:nth-child(4) {
            width: 80px;
            white-space: nowrap;
            text-align: center;
          }

          th:nth-child(5), td:nth-child(5) {
            width: 60px;
            text-align: center;
          }

          th:nth-child(6), td:nth-child(6) {
            width: 60px;
            text-align: center;
          }

          /* Hide UI elements */
          .round-selector,
          .btn-icon,
          button {
            display: none !important;
          }

          /* Legend styling */
          .standings-legend {
            margin-top: 32px;
            padding: 14px 18px;
            background: linear-gradient(to right, #f5f5f5 0%, #fafafa 100%);
            border-left: 3px solid #3a3a3a;
            font-size: 11px;
            color: #5a5a5a;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          }

          .standings-legend p {
            margin: 4px 0;
            line-height: 1.5;
          }

          .standings-legend strong {
            color: #0a0a0a;
            font-weight: 600;
          }

          /* Knockout Bracket Print Styles */
          .knockout-bracket-container {
            background: white;
            padding: 0;
          }

          .bracket-header {
            display: none !important;
          }

          .knockout-bracket {
            display: flex;
            gap: 40px;
            padding: 20px 0;
            overflow: visible;
          }

          .bracket-round {
            display: flex;
            flex-direction: column;
            min-width: 220px;
          }

          .bracket-round-title {
            text-align: center;
            margin-bottom: 20px;
            font-size: 16px;
            font-weight: 600;
            color: #0a0a0a;
            padding: 10px 14px;
            background: #fafafa;
            border-left: 3px solid #3a3a3a;
            letter-spacing: -0.1px;
          }

          .bracket-matches {
            display: flex;
            flex-direction: column;
            gap: 30px;
          }

          .bracket-match {
            background: white;
            border: 1px solid #e5e5e5;
            border-radius: 6px;
            page-break-inside: avoid;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          }

          .bracket-player {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 14px;
            border-bottom: 1px solid #ececec;
          }

          .bracket-player:last-child {
            border-bottom: none;
          }

          .bracket-player.winner {
            background: #fafafa;
            font-weight: 600;
            border-left: 3px solid #3a3a3a;
          }

          .bracket-player.loser {
            background: white;
            color: #8a8a8a;
          }

          .bracket-player .player-name {
            flex: 1;
            font-size: 13px;
            color: #1a1a1a;
            line-height: 1.4;
          }

          .bracket-player.loser .player-name {
            color: #8a8a8a;
          }

          .bracket-player .player-rating {
            font-size: 11px;
            color: #6a6a6a;
            margin-left: 10px;
          }

          /* Footer */
          .print-footer {
            margin-top: 48px;
            padding-top: 16px;
            font-size: 10px;
            color: #9a9a9a;
            text-align: center;
            letter-spacing: 0.3px;
          }

          /* Print optimizations */
          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }

            table {
              page-break-inside: auto;
            }

            thead {
              display: table-header-group;
            }

            tbody tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }

            .bracket-match {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${generateTournamentHeader(tournament, printOptions)}

        <h2 class="document-title">${roundInfo}</h2>

        ${standingsDiv.innerHTML
          .replace(/onclick="[^"]*"/g, '')
          .replace(/<button[^>]*>.*?<\/button>/gs, '')
        }

        ${generatePrintFooter(tournament)}

        <script>
          // Wait for images to load before printing
          var images = document.getElementsByTagName('img');
          var imagesToLoad = images.length;
          var imagesLoaded = 0;
          var printTriggered = false;

          function triggerPrint() {
            if (!printTriggered) {
              printTriggered = true;
              window.print();
              window.onafterprint = function() { window.close(); };
            }
          }

          if (imagesToLoad === 0) {
            triggerPrint();
          } else {
            // Set a timeout as fallback in case images fail to load
            setTimeout(triggerPrint, 2000);

            for (var i = 0; i < images.length; i++) {
              if (images[i].complete) {
                imagesLoaded++;
              } else {
                images[i].onload = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
                images[i].onerror = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
              }
            }
            // If all images were already complete
            if (imagesLoaded === imagesToLoad) {
              triggerPrint();
            }
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

/**
 * Print ranking (legacy function)
 */
export function printRanking(): void {
  const rankingDiv = document.querySelector('[id^="ranking-"]');
  if (!rankingDiv) return;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    const errorMsg = window.i18n ? window.i18n.t('standings.printWindowBlocked') : 'Failed to open print window. Please check your browser popup settings.';
    alert(errorMsg);
    return;
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>Initial Ranking - Print View</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }

          @page {
            size: A4;
            margin: 15mm;
          }

          body {
            font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            padding: 30px;
            color: #1a1a1a;
            line-height: 1.5;
            background: white;
          }

          h2 {
            font-size: 28px;
            font-weight: 700;
            color: #1c1c1c;
            margin-bottom: 25px;
            padding-bottom: 12px;
            border-bottom: 3px solid #2c2c2c;
          }

          h4 {
            font-size: 18px;
            margin-top: 20px;
            margin-bottom: 10px;
            color: #1f1f1f;
          }

          p { margin: 8px 0; color: #6a6a6a; }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          }

          thead {
            background: #1c1c1c;
          }

          th {
            padding: 10px 12px;
            text-align: left;
            color: #f5f5f5;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 11px;
            letter-spacing: 0.8px;
            border-right: 1px solid rgba(255,255,255,0.1);
          }

          th:last-child {
            border-right: none;
          }

          tbody tr {
            border-bottom: 1px solid #dcdcdc;
          }

          tbody tr:nth-child(even) {
            background-color: #f8f8f8;
          }

          td {
            padding: 10px 12px;
            font-size: 13px;
            color: #1f1f1f;
          }

          .standings-legend {
            margin-top: 30px;
            padding: 15px 20px;
            background: #f8f8f8;
            border-left: 4px solid #2c2c2c;
            font-size: 12px;
            color: #6a6a6a;
          }

          .standings-legend p {
            margin: 5px 0;
          }

          .standings-legend strong {
            color: #1f1f1f;
          }

          button { display: none !important; }

          .print-footer {
            margin-top: 40px;
            padding-top: 15px;
            font-size: 11px;
            color: #949494;
            text-align: center;
          }

          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }

            table {
              page-break-inside: avoid;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <h2>Initial Ranking</h2>
        ${rankingDiv.innerHTML.replace(/onclick="[^"]*"/g, '').replace(/<button.*?<\/button>/g, '')}
        <div class="print-footer">
          Generated on ${new Date().toLocaleString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        <script>
          // Wait for images to load before printing
          var images = document.getElementsByTagName('img');
          var imagesToLoad = images.length;
          var imagesLoaded = 0;
          var printTriggered = false;

          function triggerPrint() {
            if (!printTriggered) {
              printTriggered = true;
              window.print();
              window.onafterprint = function() { window.close(); };
            }
          }

          if (imagesToLoad === 0) {
            triggerPrint();
          } else {
            // Set a timeout as fallback in case images fail to load
            setTimeout(triggerPrint, 2000);

            for (var i = 0; i < images.length; i++) {
              if (images[i].complete) {
                imagesLoaded++;
              } else {
                images[i].onload = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
                images[i].onerror = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
              }
            }
            // If all images were already complete
            if (imagesLoaded === imagesToLoad) {
              triggerPrint();
            }
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

// Export to window for onclick handlers

window.printStandings = printStandings;
window.printRanking = printRanking;
